import React, { useEffect, useState } from 'react';
import { PortalShell } from '@/components/nav/PortalShell';
import { portalApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fmtMoney, fmtDate, fmtDocNumber } from '@/lib/format';
import { Receipt, Calendar, ClipboardPlus, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { JobStatusPill } from '@/components/jobs/JobStatusPill';
import { EmptyState } from '@/components/empty/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';

export default function PortalDashboardPage() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);

  useEffect(() => {
    // Failures are surfaced by the api response interceptor.
    portalApi.me().then(setMe).catch(() => {});
    portalApi.invoices()
      .then(list => setInvoices(list.filter(i => i.status !== 'paid')))
      .catch(() => {})
      .finally(() => setInvoicesLoaded(true));
    portalApi.jobs()
      .then(list => setJobs(list.filter(j => !['cancelled', 'paid', 'refunded'].includes(j.status)).slice(0, 5)))
      .catch(() => {})
      .finally(() => setJobsLoaded(true));
  }, []);

  const kpi = me?.kpi || {};

  return (
    <PortalShell title="Overview">
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold">Hi, {user?.name?.split(' ')[0]}</h1>
          <p className="text-sm text-muted-foreground">Everything about your properties in one place.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Kpi to="/portal/invoices" label="Unpaid invoices" value={kpi.unpaid_invoices ?? 0} icon={Receipt} />
          <Kpi to="/portal/schedule" label="Upcoming" value={kpi.upcoming_jobs ?? 0} icon={Calendar} />
          <Kpi to="/portal/service-requests" label="Open requests" value={kpi.open_service_requests ?? 0} icon={ClipboardPlus} />
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Open invoices</CardTitle>
            <Link to="/portal/invoices" className="text-xs font-medium text-primary hover:underline">View all</Link>
          </CardHeader>
          <CardContent>
            {!invoicesLoaded ? <RowsSkeleton /> : invoices.length === 0 ? <div className="text-sm text-muted-foreground text-center py-4">All caught up ✨</div> : (
              <div className="divide-y">
                {invoices.slice(0, 5).map(i => (
                  <Link key={i.id} to={`/portal/invoices/${i.id}`} className="flex items-center justify-between py-3 hover:bg-muted/40 -mx-2 px-2 rounded-md">
                    <div>
                      <div className="text-sm font-medium">{fmtDocNumber(i, 'Invoice')}</div>
                      <div className="text-xs text-muted-foreground">{fmtDate(i.created_at)}</div>
                    </div>
                    <div className="font-semibold tabular-nums">{fmtMoney(i.total)}</div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Upcoming cleanings</CardTitle>
            <Link to="/portal/schedule" className="text-xs font-medium text-primary hover:underline">View schedule</Link>
          </CardHeader>
          <CardContent>
            {!jobsLoaded ? <RowsSkeleton /> : jobs.length === 0 ? <EmptyState icon={Calendar} title="No upcoming cleanings" description="When we schedule your next turnover it will appear here." /> : (
              <div className="divide-y">
                {jobs.map(j => (
                  <div key={j.id} className="flex items-center justify-between py-3">
                    <div>
                      <div className="text-sm font-medium">{j.property_nickname || 'Cleaning'}</div>
                      <div className="text-xs text-muted-foreground">{fmtDate(j.scheduled_at)}</div>
                    </div>
                    <JobStatusPill status={j.status} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PortalShell>
  );
}

function RowsSkeleton({ rows = 3 }) {
  return (
    <div className="divide-y">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center justify-between gap-3 py-3">
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/5" />
          </div>
          <Skeleton className="h-5 w-16 shrink-0" />
        </div>
      ))}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, to }) {
  return (
    <Link to={to}>
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">{label}</div>
            <div className="h-8 w-8 rounded-lg bg-accent text-accent-foreground flex items-center justify-center"><Icon className="h-4 w-4" /></div>
          </div>
          <div className="mt-2 font-display text-2xl md:text-3xl font-semibold tabular-nums">{value}</div>
        </CardContent>
      </Card>
    </Link>
  );
}
